import { BathroomIcon } from '@/components/icons/BathroomIcon'
import { BedIcon } from '@/components/icons/BedIcon'
import { GarageIcon } from '@/components/icons/GarageIcon'
import { GymIcon } from '@/components/icons/GymIcon'
import { IconType } from '@/components/icons/IconType'
import { PoolIcon } from '@/components/icons/PoolIcon'
import { StudyIcon } from '@/components/icons/StudyIcon'
import { TennisIcon } from '@/components/icons/TennisIcon'
import { Listing } from '@/data/getListing'

/**
 * Parses the entire listing record as attributes are over meta, attributes and features.
 * @param listing Listing
 * @param formatted Can return just the text, or React.ReactNode if true
 * @param keyList What attributes to search for
 * @returns
 */
export const parseAttributes = (
  listing: Listing,
  formatted = true,
  keyList = ['bedrooms', 'bathrooms', 'garages', 'gym', 'study', 'pool', 'tennis', 'landarea_m2', 'buildarea_m2'],
): [keyof Listing['attributes'], string, React.ReactNode, IconType | undefined][] => {
  const { attributes, meta } = listing
  const attrs = []
  // order
  if (attributes.bedrooms !== null && keyList.includes('bedrooms')) {
    attrs.push(['bedrooms', 'Bedrooms', attributes.bedrooms, icons['bedrooms']])
  }
  if (attributes.bathrooms !== null && keyList.includes('bathrooms')) {
    attrs.push(['bathrooms', 'Bathrooms', attributes.bathrooms, icons['bathrooms']])
  }
  if (keyList.includes('garages')) {
    if (attributes.total_car_accom !== null) {
      attrs.push(['garages', 'Car spaces', attributes.total_car_accom, icons['garages']])
    } else if (attributes.garages !== null) {
      attrs.push(['garages', 'Car spaces', attributes.garages, icons['garages']])
    } else if (attributes.carports !== null) {
      attrs.push(['garages', 'Car spaces', attributes.carports, icons['garages']])
    }
  }

  if (meta?.rates_council && keyList.includes('rates_council')) {
    attrs.push(['council_rates', 'Council rates', meta.rates_council, undefined])
  }
  if (meta?.rates_water && keyList.includes('rates_water')) {
    attrs.push(['rates_water', 'Water rates', meta.rates_water, undefined])
  }
  if (meta?.rates_bodycorp && keyList.includes('rates_bodycorp')) {
    attrs.push(['rates_bodycorp', 'Body Corp', meta.rates_bodycorp, undefined])
  }

   
  ;['Gym', 'Study', 'Pool', 'Tennis']
    .filter(
      (attr) =>
        keyList.includes(attr.toLowerCase()) &&
        listing.features.some((feature) => feature.match(new RegExp(attr, 'i'))),
    )
    .forEach((attr) => {
      attrs.push([attr.toLowerCase(), attr, 1, icons[attr.toLowerCase() as keyof typeof icons]])
    })

  if ((attributes.landarea_m2 || attributes.landarea) && keyList.includes('landarea_m2')) {
    attrs.push([
      'landarea_m2',
      'Land size',
      formatted
        ? formatArea(attributes.landarea_m2, attributes.landarea, attributes.landarea_unit, {
            treatSmallDecimalAsHectares: true,
          })
        : attributes.landarea_m2,
      icons['landarea_m2'],
    ])
  } else if ((attributes.buildarea_m2 || attributes.buildarea) && keyList.includes('buildarea_m2')) {
    attrs.push([
      'buildarea_m2',
      'Build size',
      formatted
        ? formatArea(attributes.buildarea_m2, attributes.buildarea, attributes.buildarea_unit)
        : attributes.buildarea_m2,
      icons['buildarea_m2'],
    ])
  }

  return attrs as [keyof Listing['attributes'], string, React.ReactNode, IconType | undefined][]
}

const icons: Partial<Record<keyof Listing['attributes'] | 'gym' | 'study' | 'pool' | 'tennis', IconType>> = {
  bedrooms: BedIcon,
  bathrooms: BathroomIcon,
  garages: GarageIcon,
  gym: GymIcon,
  study: StudyIcon,
  pool: PoolIcon,
  tennis: TennisIcon, // Coming soon!
} as const

const formatArea = (
  areaM2: string | null,
  area: string | null,
  unit: string | null,
  options: { treatSmallDecimalAsHectares?: boolean } = {},
) => {
  const areaM2Value = areaM2 ? Number(areaM2) : null
  const areaValue = area ? Number(area) : null
  const isSmallDecimal = areaM2Value !== null && areaM2Value > 0 && areaM2Value < 100 && !Number.isInteger(areaM2Value)

  if (unit === 'ha' || (options.treatSmallDecimalAsHectares && isSmallDecimal)) {
    const hectareValue = areaValue ?? (areaM2Value === null ? null : isSmallDecimal ? areaM2Value : areaM2Value / 10000)

    if (hectareValue === null || Number.isNaN(hectareValue)) {
      return null
    }

    return (
      <>
        {hectareValue.toLocaleString('en-AU', { maximumFractionDigits: 3 })}ha
      </>
    )
  }

  const value = areaM2 ?? area

  if (!value || Number.isNaN(Number(value))) {
    return null
  }

  return (
    <>
      {Math.round(Number(value)).toLocaleString('en-AU')}m<sup>2</sup>
    </>
  )
}
